import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { ganzeSeiteZu } from "@/lib/navigation"

/**
 * Nutzt die bestehende /api/v1/dashboard-Abfrage als Auth-Probe: wenn 200
 * kommt, ist der User eingeloggt; 401 → nicht eingeloggt.
 *
 * Die Session selbst lebt im admin_session Cookie (signed per itsdangerous).
 * Login läuft über den bestehenden FastAPI-Endpoint POST /admin/login.
 */
export function useAuth() {
  const qc = useQueryClient()

  const session = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const { response } = await api.GET("/api/v1/dashboard", {})
      return response.status === 200
    },
    retry: false,
    staleTime: 1000 * 60 * 5,  // 5 Min
  })

  const login = useMutation({
    mutationFn: async (creds: { username: string; password: string; code?: string }) => {
      const form = new URLSearchParams()
      form.append("username", creds.username)
      form.append("password", creds.password)
      form.append("code", creds.code || "")  // 2FA-Code (falls aktiviert)
      const res = await fetch("/admin/login", {
        method: "POST",
        credentials: "include",
        body: form,
      })
      if (res.status >= 400) {
        throw new Error(`Login fehlgeschlagen (HTTP ${res.status})`)
      }
      return true
    },
    // 🔴 Warum hier zwei Dinge passieren und das zweite ZURUeCKGEGEBEN wird:
    //
    // Bis zum 17.08.2026 musste man sich zweimal hintereinander anmelden. Der
    // erste Versuch war nie falsch — das Cookie sass bereits. Nur wusste die
    // Oberflaeche es noch nicht:
    //
    //   1. `invalidateQueries` stiess ein Nachladen an, wurde aber nicht
    //      abgewartet. Die Session-Abfrage behielt solange ihren ALTEN Wert.
    //   2. `Login.tsx` navigierte sofort nach `/app`.
    //   3. `ProtectedRoute` fragte `isAuthenticated` — noch `false`. Und
    //      `isLoading` war es auch nicht mehr, denn Daten waren ja da (nur
    //      veraltete). Also zurueck auf `/login`, Formular leer.
    //
    // `setQueryData` beantwortet den naechsten Render SOFORT richtig — ohne
    // auf eine weitere Abfrage zu warten. Das ist der eigentliche Fix: der
    // Login war ja erfolgreich, wir wissen es bereits.
    //
    // Das `invalidateQueries` bleibt als Gegenprobe, aber bewusst OHNE
    // `return`/`await`: sonst haengt `mutateAsync` an einer Netzrunde, und
    // der Anmeldeknopf steht laenger als noetig auf „Anmelden...".
    onSuccess: () => {
      qc.setQueryData(["session"], true)
      void qc.invalidateQueries({ queryKey: ["session"] })
    },
  })

  // 🔴 Abmelden laedt die Seite neu — ein SPA-Wechsel reicht hier NICHT.
  //
  // Gemeldet am 25.08.2026 von Paul: „man kann sich nicht ausloggen. man ist
  // dann immernoch auf der plattformseite aber es kommen 401 error."
  //
  // Das Cookie war jedesmal weg — der Riegel funktionierte. Haengen blieb die
  // Oberflaeche: `qc.clear()` wirft die Abfragen aus dem Zwischenspeicher,
  // aber die bereits haengenden Beobachter bekommen davon NICHTS mit. Sie
  // halten ihr letztes Ergebnis fest, und `ProtectedRoute` hielt damit ewig
  // ein `isAuthenticated === true` in der Hand. Es rendert auch nicht neu,
  // wenn man im Menue weiterklickt: es umschliesst das Layout, nicht die
  // einzelne Seite. Alle anderen Abfragen liefen derweil weiter und holten
  // sich 401 um 401 — genau das Bild aus der Meldung.
  //
  // 🔑 Das ganze Neuladen ist hier nicht die grobe, sondern die richtige
  // Loesung: es beendet jeden Abfrage-Takt, raeumt die Kundendaten des
  // Abgemeldeten aus dem Arbeitsspeicher und laesst die Anmeldung von vorn
  // beurteilen. Ein abgemeldeter Zustand, der noch Reste des angemeldeten
  // traegt, ist kein abgemeldeter Zustand.
  const logout = useMutation({
    mutationFn: async () => {
      await fetch("/admin/logout", { credentials: "include" })
      return true
    },
    // `onSettled`, nicht `onSuccess`: wer auf „Logout" klickt, will weg —
    // auch wenn die Antwort im Netz haengen bleibt. Auf der Anmeldeseite
    // sieht er dann, ob es gereicht hat.
    onSettled: () => {
      qc.clear()
      ganzeSeiteZu("/login")
    },
  })

  return {
    isAuthenticated: session.data === true,
    isLoading: session.isLoading,
    login,
    logout,
  }
}
